import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RedirectHandlerService } from '../services/redirect-handler.service';
import { UserContextService } from 'src/services/user-context.service';
import { AuthenticationHttpService } from 'src/providers/authentication-http.service';
import { RoutePermission } from 'src/app/core/models/route-permission-key';

/**
 * Smart Redirect Guard
 *
 * Handles complex redirects that require runtime logic, replacing UI-Router's dynamic rules.
 * This guard is used for routes that need intelligent redirection based on:
 * - User permissions
 * - User context/role
 * - Feature flags
 * - Authentication state
 *
 * Usage in Routes:
 * {
 *   path: 'smart-dashboard',
 *   canActivate: [SmartRedirectGuard],
 *   data: { redirectType: 'dashboard' }
 * }
 *
 * Migration Notes:
 * - Replaces complex UI-Router rule functions with Angular Router guard logic
 * - Provides better error handling and debugging than UI-Router rules
 * - Supports async operations (user context loading) before redirecting
 */
@Injectable({
    providedIn: 'root'
})
export class SmartRedirectGuard implements CanActivate {
    constructor(
        private router: Router,
        private redirectHandler: RedirectHandlerService,
        private userContextService: UserContextService,
        private authService: AuthenticationHttpService
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        // Get redirect type from route data
        const redirectType = route.data?.['redirectType'] as string;

        // Ensure user is authenticated before any redirects
        if (!this.authService.isAuthenticated()) {
            return this.router.createUrlTree(['/login'], {
                // LoginComponent restores the destination from 'redirectRoute' in dotted
                // UI-Router state format (e.g. "dashboard.activity"), not a slashed path.
                // Match EnhancedAuthGuard.redirectToLogin so post-login return works.
                queryParams: { redirectRoute: this.toRedirectRoute(state.url) }
            });
        }

        // Ensure user context is loaded before making permission-based decisions
        if (!this.userContextService.isUserContextLoaded()) {
            return this.userContextService.loadUserContextAsObservable().pipe(
                map(() => this.handleRedirect(redirectType, route)),
                catchError(() => {
                    console.error('SmartRedirectGuard: Failed to load user context');
                    return of(this.router.createUrlTree(['/error']));
                })
            );
        }

        // User context is loaded, handle redirect immediately
        return this.handleRedirect(redirectType, route);
    }

    private handleRedirect(redirectType: string, route: ActivatedRouteSnapshot): boolean | UrlTree {
        switch (redirectType) {
            case 'dashboard':
                return this.handleDashboardRedirect(route);

            case 'network':
                return this.handleNetworkRedirect();

            case 'profile':
                return this.handleProfileRedirect();

            case 'postLogin':
                return this.handlePostLoginRedirect(route.queryParams['returnUrl']);

            case 'legacy':
                return this.handleLegacyRedirect(route);

            case 'feature':
                return this.handleFeatureRedirect(route);

            default:
                console.warn(`SmartRedirectGuard: Unknown redirect type: ${redirectType}`);
                return this.router.createUrlTree(['/dashboard/activity']);
        }
    }

    private handleDashboardRedirect(route: ActivatedRouteSnapshot): UrlTree {
        // Determine best dashboard route based on permissions
        const hasActivityPerm = this.userContextService.hasPermissionToVisitRoute(RoutePermission.dashboardActivity);
        const hasLicensesPerm = this.userContextService.hasPermissionToVisitRoute(RoutePermission.dashboardLicenses);
        const hasCompliancePerm = this.userContextService.hasPermissionToVisitRoute(RoutePermission.dashboardCompliance);

        // Default to /dashboard/activity for all account types unless explicitly forbidden
        // Note: dashboard.activity has requiresPermission: false in Angular Router config
        // The hasPermissionToVisitRoute() check returns undefined for routes without explicit permissions
        // which means "not forbidden" - we should allow access
        if (hasActivityPerm !== false) {  // undefined or true = allowed
            // Clear auxiliary outlets so OutletManagementService can set the correct ones
            return this.router.createUrlTree(['/dashboard/activity'], {
                queryParams: route.queryParams
            });
        } else if (hasLicensesPerm === true) {
            return this.router.createUrlTree(['/dashboard/licenses'], {
                queryParams: route.queryParams
            });
        } else if (hasCompliancePerm === true) {
            return this.router.createUrlTree(['/dashboard/compliance'], {
                queryParams: route.queryParams
            });
        } else {
            return this.router.createUrlTree(['/profile/overview'], {
                queryParams: route.queryParams
            });
        }
    }

    private handleNetworkRedirect(): UrlTree {
        // Use the redirect handler service for network logic
        if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkEntities)) {
            return this.router.createUrlTree(['/network/entities']);
        } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkAgencyList)) {
            return this.router.createUrlTree(['/network/agencyList']);
        } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkCarrierList)) {
            return this.router.createUrlTree(['/network/carrierList']);
        } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkHierarchyList)) {
            return this.router.createUrlTree(['/network/hierarchyList']);
        } else {
            return this.router.createUrlTree(['/error']);
        }
    }

    private handleProfileRedirect(): UrlTree {
        // Profile redirect based on user type
        const userContext = this.userContextService.userContext;

        if (userContext?.account?.accountType === 'individual') {
            return this.router.createUrlTree(['/profile/overview']);
        } else if (userContext?.account?.accountType === 'agency') {
            return this.router.createUrlTree(['/profile/licenses']);
        } else {
            return this.router.createUrlTree(['/profile/overview']);
        }
    }

    /**
     * Convert an Angular Router path to the dotted UI-Router state name LoginComponent
     * expects, e.g. "/dashboard/activity?x=1" -> "dashboard.activity". Mirrors
     * EnhancedAuthGuard.redirectToLogin so both guards produce the same 'redirectRoute'.
     */
    private toRedirectRoute(url: string): string {
        const cleanUrl = url.split('?')[0];
        return cleanUrl.substring(1).replace(/\//g, '.');
    }

    private handlePostLoginRedirect(returnUrl?: string): UrlTree {
        if (returnUrl) {
            // returnUrl is a full path (e.g. "/dashboard/activity"); parse it so the
            // slashes become real path segments rather than a single encoded segment.
            return this.router.parseUrl(returnUrl);
        }

        // Default post-login redirect logic
        return this.router.parseUrl(this.redirectHandler.getRedirectUrl());
    }

    private handleLegacyRedirect(route: ActivatedRouteSnapshot): UrlTree {
        // Handle legacy route redirects with parameter preservation
        const newPath = route.data?.['newPath'] as string;

        if (!newPath) {
            console.error('SmartRedirectGuard: No newPath specified for legacy redirect');
            return this.router.createUrlTree(['/dashboard/activity']);
        }

        return this.router.createUrlTree([newPath], {
            queryParams: route.queryParams,
            fragment: route.fragment
        });
    }

    private handleFeatureRedirect(route: ActivatedRouteSnapshot): UrlTree {
        // Feature flag based redirects
        const featureName = route.data?.['featureName'] as string;
        const enabledPath = route.data?.['enabledPath'] as string;
        const disabledPath = route.data?.['disabledPath'] as string;

        if (!featureName || !enabledPath || !disabledPath) {
            console.error('SmartRedirectGuard: Missing feature redirect configuration');
            return this.router.createUrlTree(['/dashboard/activity']);
        }

        const isEnabled = this.userContextService.isFeatureToggleEnabled(featureName);
        return this.router.createUrlTree([isEnabled ? enabledPath : disabledPath]);
    }
}
